import { ReactElement } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ISiteListCardProps } from '.';
import { GlueUsedLabel } from '@components/lms/addNewSite/components/glueDetails';
import { SiteTypeLabel } from '@components/lms/addNewSite/components/siteInformation';
import { useSiderToggleMenu } from '@store/useSiderToggleMenu';
export const SiteListCard = (props: ISiteListCardProps): ReactElement => {
	const { site, isContractorSite = false } = props;
	const navigate = useNavigate();
	const { setFullPageLoader } = useSiderToggleMenu();
	const location = useLocation();
	const isContractorRoute = location.pathname === '/lms/contractor' || location.pathname === '/contractor';

	const phoneNumber = site?.end_user_phone_c || site?.applicator_phone_number_text_c;
	const isCompetition = site?.solvent_used_c === GlueUsedLabel.Competition;

	const handleNavigate = (): void => {
		navigate('/lms/site-overview', {
			state: {
				siteId: site?.id,
			},
		});
	};

	const handleGetDirections = (destLat: number, destLng: number): void => {
		setFullPageLoader(true);

		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const sourceLat = position.coords.latitude;
					const sourceLng = position.coords.longitude;

					const directionUrl = `https://www.google.com/maps/dir/?api=1&origin=${sourceLat},${sourceLng}&destination=${destLat},${destLng}&travelmode=driving`;

					window.open(directionUrl, '_blank');
					setFullPageLoader(false);
				},
				(error) => {
					setFullPageLoader(false);
					alert(`Unable to get current location` + (error.message ? `: ${error.message}` : ''));
				}
			);
		} else {
			setFullPageLoader(false);
			alert('Geolocation is not supported by this browser.');
		}
	};

	return (
		<li className="d-col d-col-2">
			<div className="card-body" role="button" tabIndex={0} onClick={handleNavigate} aria-label={site?.name}>
				<div className="card-title">
					<div className="title-text">
						<address className="h3" title={site?.name}>
							{site?.name}
						</address>
						{isContractorSite && <span className="h6">{site?.site_location_c}</span>}
					</div>

					<div className="action-panel">
						<button
							type="button"
							aria-label="Directions"
							className="rounded-btn primary"
							onClick={(e) => {
								e.stopPropagation();
								handleGetDirections(site.latitude, site.longitude);
							}}
						>
							<span className="icon-direction"></span>
							Directions
						</button>

						{!isContractorSite && (
							<Link
								to={phoneNumber ? `tel:+91${phoneNumber}` : '#'}
								aria-label="call user"
								className="contact-info"
								onClick={(e) => e.stopPropagation()}
							>
								<span className="icon-call"></span>
							</Link>
						)}
					</div>
				</div>

				<div className="site-info">
					<div className="site-type">
						{site?.site_sub_type_c && (
							<span className="rounded-btn primary">
								<span
									className={
										site?.site_sub_type_c === SiteTypeLabel.Residential ? 'icon-home-filled' : 'icon-buildings'
									}
								></span>
								{site?.site_sub_type_c}
							</span>
						)}
						{isCompetition && (
							<span className="rounded-btn secondary">
								<span className="icon-electric"></span>
								{site?.solvent_used_c}
							</span>
						)}
					</div>

					<div className="label-name-details site-id">Site ID: {site?.id}</div>
					<div className="glue-info">
						<div className="common-label-style">
							{isCompetition ? (
								<span className="label-name h4">Conversion Potential: {site?.epoxy_adhesive_used_c} KG</span>
							) : (
								<span className="label-name h4">Glue Potential: {site?.fevicol_business_potential_c} KG</span>
							)}
							{site.site_current_status_c !== 'Upcoming' && (
								<span className="label-name h4">Product Used: {site?.competition_brand_c}</span>
							)}
						</div>

						{(site?.priority || site.site_priority) && (
							<div className="priority">
								<span className="rounded-btn secondary-outline">Priority {site?.priority || site.site_priority}</span>
							</div>
						)}
						<div className="site-user-info-wrapper">
							{site.lead_status === 'Enrolled' && (
								<span className="rounded-btn green-outline enrolled-status">{site.lead_status}</span>
							)}

							{site.lead_status === 'Pending' && (
								<span className="rounded-btn orange-outline pending-status">{site.lead_status}</span>
							)}

							{site.lead_status === 'Rejected' && (
								<span className="rounded-btn red-outline rejected-status">
									{site.lead_status} - {site.non_end_category_c}
								</span>
							)}
						</div>
					</div>

					<div className="site-info-bottom">
						{!isContractorRoute && <div className="label-name-details">Contractor Name: {site?.applicatorname_c}</div>}
						{!isContractorRoute && (
							<div className="label-name-details">
								Membership No.: {site?.membership_no ?? site?.lead_membership_no}
							</div>
						)}

						{site?.solvent_used_c === 'Competition' && !isContractorRoute && (
							<div className="label-name-details">Site Specialist: {site?.source}</div>
						)}
					</div>
				</div>

				{site?.lastactivitydate && (
					<div className="site-visit-info">
						<span className="label-name h5">Last Visit: {site?.lastactivitydate}</span>
						{site?.last_updated_on_tsi_cmdi_asi_c && (
							<>
								<span className="pipe">|</span>
								<span className="label-name h5">Next Scheduled Visit:{site?.last_updated_on_tsi_cmdi_asi_c}</span>
							</>
						)}
					</div>
				)}
			</div>
		</li>
	);
};
